/**
 * Reconcile a room's REAL `table` rows against the desk slots its resolved
 * furniture layout calls for.
 *
 * Labelling a room (or re-labelling it, or picking a different-size layout
 * for the same space type) must never stack a second set of desks on top of
 * whatever is already there — the rule this module implements is RECONCILE,
 * never duplicate:
 *
 *   1. Match each target desk slot to an existing table of the same capacity,
 *      preferring a table that already carries a job so a person's desk is
 *      the last thing touched.
 *   2. Any target slot left unmatched needs a new table created.
 *   3. Any existing table left unmatched is surplus. If nobody is assigned to
 *      it, delete it. If someone IS assigned to it, keep it anyway — this
 *      function only ever creates or deletes; it never reassigns or unseats
 *      a person, so a shrinking room can leave a job "over-provisioned"
 *      rather than orphan an assignment.
 *
 * Re-running this with the same inputs is idempotent: every existing table
 * matches a target slot, so nothing is created and nothing is deleted.
 *
 * Pure — no network, no React — so the plan can be asserted on directly and
 * the caller (FloorLayoutDesigner) does nothing but execute it.
 */

/**
 * @param {Array<{table_id:number, capacity:number, tableJobs?:Array}>} existingTables
 * @param {number[]} targetCapacities one entry per desk-bearing template item
 * @returns {{ keepIds:number[], toCreate:number[], toDeleteIds:number[], keptExtraIds:number[] }}
 */
export function planTableReconciliation(existingTables, targetCapacities) {
  const remaining = [...targetCapacities].sort((a, b) => b - a);
  const assignedPool = (existingTables || []).filter((t) => (t.tableJobs ?? []).length > 0);
  const unassignedPool = (existingTables || []).filter((t) => (t.tableJobs ?? []).length === 0);

  const keepIds = [];
  const matchOneFrom = (pool) => {
    for (let i = 0; i < remaining.length; i++) {
      const idx = pool.findIndex((t) => Number(t.capacity) === remaining[i]);
      if (idx === -1) continue;
      keepIds.push(pool[idx].table_id);
      pool.splice(idx, 1);
      remaining.splice(i, 1);
      return true;
    }
    return false;
  };
  // Assigned tables get first claim on a matching slot, so a swap in template
  // size never has to touch a desk someone is already sitting at.
  while (matchOneFrom(assignedPool)) {
    /* keep matching */
  }
  while (matchOneFrom(unassignedPool)) {
    /* keep matching */
  }

  return {
    keepIds,
    toCreate: remaining, // capacities still unmatched — create these
    toDeleteIds: unassignedPool.map((t) => t.table_id), // surplus, no job: safe to delete
    keptExtraIds: assignedPool.map((t) => t.table_id), // surplus, has a job: never deleted
  };
}
